import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { makeApiCall } from "src/api/apiRequests";
import { PermissionCheck } from "src/helpers/PermissionCheck";
import { useUserdata } from "src/store/UserData";
import { useMessageNotificationCount } from "src/store/chat/MessageNotifications";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";

import * as ConfirmPopup from "../../../helpers/ConfirmPopup";
import CreateNewConversation from "./CreateNewConversation";
import API_PATHS from "src/api/apiPaths";
import PATHS from "src/routes/Paths";
import API_HEADERS from "src/api/apiConfig";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import CreateGroupChat from "./CreateGroupChat";
import moment from "moment";
import ThreadMessages from "./ThreadMessages";
import SimpleBar from "simplebar-react";
import EditThread from "./EditThread";
import SearchFilter from "src/helpers/SearchFilter";
import Loading from "src/helpers/Loading";

const Chat: React.FC = () => {
  const { t } = useTranslation("");
  const { threadId } = useParams();
  const navigate = useNavigate();

  const userData = useUserdata((state: any) => state.userData);

  const addMessagesCount = useMessageNotificationCount(
    (state: any) => state.addMessagesCount
  );

  const messagesCount = useMessageNotificationCount(
    (state: any) => state.messagesCount
  );
  const ChatLogo: string =
    require("../../../assets/images/logo/chat-logo.svg").default;

  // States
  const ref = useRef<null>(null);
  const refS = useRef<null>(null);
  const [search, setSearch] = useState("");
  const [filteredThreads, setFilteredThreads] = useState<any[]>([]);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [chatList, setChatList] = useState<any[]>([]);
  //Dropdown
  const [dropdown, setDropdown] = useState(false);
  const [dropdownId, setDropdownId] = useState<number | null>(null);
  //Modals
  const [createChatModal, setCreateChatModal] = useState(false);
  const [createGroup, setCreateGroup] = useState(false);
  const [modalId, setModalId] = useState<number | null>(1);
  const [showEditThread, setShowEditThread] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchLoading, setSearchLoading] = useState(false);

  const getChatList = async () => {
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.messagesList,
        "GET",
        API_HEADERS.authenticated
      );

      setChatList(response.threads);
      addMessagesCount(response.total_unread_messages);
      setLoading(false);
    } catch (error: any) {
      if (error.response.status === 403) {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
        navigate(-1);
      } else if (error.response.status === 404) {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
        navigate(-1);
      }
    }
  };

  useEffect(() => {
    getChatList();
  }, [messagesCount]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value;
    setSearch(searchTerm);

    if (timeoutId) clearTimeout(timeoutId);
    setSearchLoading(true);

    const newTimeoutId = setTimeout(() => {
      if (searchTerm.length > 2) {
        filterMessages(searchTerm);
      } else {
        setFilteredThreads([]);
      }
    }, 500);

    setTimeoutId(newTimeoutId);
  };

  const deleteThread = (threadId: number, threadTitle: string) => {
    ConfirmPopup.deletePopup(
      t("delete_thread_title"),
      t("delete_thread_type"),
      threadTitle
    ).then(async (response) => {
      if (response.isConfirmed) {
        try {
          const response: any = await makeApiCall<ResponseType>(
            API_PATHS.deleteThread(threadId),
            "DELETE",
            API_HEADERS.authenticated
          );
          getChatList();
          FLASH_MESSAGES.successMsg(response.success);
          navigate(PATHS.chat);
        } catch (error: any) {
          if (error.response.data.error) {
            FLASH_MESSAGES.errorMsg(error.response.data.error);
          }
        }
      }
    });
  };

  const filterMessages = async (searchTerm: string) => {
    const searchParams: any = { term: searchTerm };
    let request: any = API_PATHS.filterMessages;
    request = SearchFilter(searchParams, request);

    try {
      const response: any = await makeApiCall<ResponseType>(
        request,
        "GET",
        API_HEADERS.authenticated
      );
      setSearchLoading(false);
      const seenThreads = new Set();
      const filteredThreads = response.threads.filter((thread: any) => {
        // Check if the thread subject contains the search term
        const subjectMatch = thread.subject
          ? thread.subject.toLowerCase().includes(searchTerm.toLowerCase())
          : false;

        // Check if any message in the thread contains the search term
        const messageMatch = thread.messages.some((message: any) =>
          message.body.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // If either the subject or any message matches, include the thread
        if ((subjectMatch || messageMatch) && !seenThreads.has(thread.id)) {
          seenThreads.add(thread.id); // Add thread ID to the Set to avoid duplicates
          return true;
        }
        return false;
      });

      setFilteredThreads(filteredThreads);
    } catch (error: any) {
      setSearchLoading(true);

      if (error.response.status === 403) {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
        navigate(-1);
      } else if (error.response.status === 404) {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
        navigate(-1);
      }
    }
  };

  return (
    <>
      <div className="chat-overview">
        <div className={`chat-table ${threadId ? "active-chat" : ""}`}>
          <div className="head">
            <div className="form-block col-11 col-md-10">
              <i className="icon search-icon  mt-1" />
              <input
                type="text"
                name="search"
                className={"form-input-search "}
                onChange={handleSearchChange}
                value={search}
                placeholder={t("search_term_name")}
                autoComplete="off"
              />
            </div>

            {search.length > 2 && (
              <>
                <ul className="search-suggestions mt-5 ">
                  {searchLoading ? (
                    <div className="text-center">
                      <Loading />
                    </div>
                  ) : filteredThreads.length > 0 ? (
                    filteredThreads.map((thread: any) => (
                      <>
                        <SimpleBar
                          ref={refS}
                          style={{ maxHeight: "250px" }}
                          className="search-suggestion-item"
                        >
                          <li
                            key={thread.id}
                            onClick={() => {
                              navigate(PATHS.messagesOfThreads(thread.id));
                              setSearch("");
                            }}
                          >
                            {thread.type === "group" ? (
                              thread.subject ? (
                                <strong>{thread.subject}</strong>
                              ) : (
                                ""
                              )
                            ) : (
                              <strong>{thread.chat_with}</strong>
                            )}
                            {thread.messages
                              .filter((message: any) =>
                                message.body
                                  .toLowerCase()
                                  .includes(search.toLowerCase())
                              )
                              .map((message: any, idx: number) => (
                                <div key={idx}>
                                  <small>{message.body}</small>
                                </div>
                              ))}
                          </li>
                        </SimpleBar>
                      </>
                    ))
                  ) : (
                    <li className="search-suggestion-item">
                      {t("no_results_found")}
                    </li>
                  )}
                </ul>
              </>
            )}

            {PermissionCheck("message-thread-create") ? (
              <Dropdown isOpen={dropdown} toggle={() => setDropdown(!dropdown)}>
                <DropdownToggle caret={false} tag="span">
                  <span
                    className="d-inline-flex"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDropdown(!dropdown);
                    }}
                  >
                    <i className="icon actions-icon mt-2 " />
                  </span>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                  <DropdownItem
                    onClick={(e) => {
                      setCreateChatModal(true);

                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <i className="icon chat-create-icon" />
                    <span>{t("new_conversation")}</span>
                  </DropdownItem>
                  {createChatModal && (
                    <CreateNewConversation
                      showModal={createChatModal}
                      setModified={() => {
                        getChatList();
                      }}
                      setShowModal={(e: any) => setCreateChatModal(e)}
                    />
                  )}
                  <DropdownItem
                    onClick={(e) => {
                      setCreateGroup(true);
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <i className="icon create-chat-group" />
                    <span>{t("new_group")}</span>
                  </DropdownItem>
                  {createGroup && (
                    <CreateGroupChat
                      onShowModal={createGroup}
                      setModified={() => {
                        getChatList();
                      }}
                      setShowModal={(e: any) => setCreateGroup(e)}
                    />
                  )}
                </DropdownMenu>
              </Dropdown>
            ) : (
              ""
            )}
          </div>
          {loading ? (
            <div className="mt-4 text-center">
              <Loading />
            </div>
          ) : PermissionCheck("message-thread-list") ? (
            <SimpleBar ref={ref} className="simplebar-chat-table">
              {chatList.map((item: any) => {
                const creator =
                  item.participants.length > 0
                    ? item.participants[0].user_id
                    : null;

                // const unreadCount = messageThreadCount[item.id] || 0;

                return (
                  <div
                    className={`main-section ${
                      item.id == threadId ? "active" : ""
                    }`}
                    key={item.id}
                    onClick={() => {
                      navigate(PATHS.messagesOfThreads(item.id));
                    }}
                  >
                    <div
                      className="avatar avatar-blue size-sm"
                      style={{ alignSelf: "center" }}
                    >
                      {item.type === "group" ? (
                        <span
                          className="circle"
                          style={{ width: "40px", height: "40px" }}
                        >
                          <i className="icon group-icon" />
                        </span>
                      ) : (
                        <span
                          className="circle"
                          style={{ width: "40px", height: "40px" }}
                        >{`${item.chat_with ? item.chat_with.charAt(0) : ""}
                    
                    `}</span>
                      )}
                    </div>
                    <div className="table-cell">
                      <div className="cell-style">
                        <span className="bold-text">
                          {item.type === "group"
                            ? item.subject
                              ? item.subject.length > 20
                                ? item.subject.substring(0, 20) + "..."
                                : item.subject
                              : ""
                            : item.chat_with}
                        </span>

                        <span className="time-text">
                          {item.latest_message ? (
                            <span className="time-text">
                              {`${moment(item.latest_message.created_at).format(
                                "yyy.MM.DD"
                              )}`}
                            </span>
                          ) : (
                            ""
                          )}
                        </span>
                        <span className="time-text ">
                          {item.latest_message ? (
                            <span className="time-text">
                              {`${moment(item.latest_message.created_at).format(
                                "hh:mm A"
                              )}`}
                            </span>
                          ) : (
                            ""
                          )}
                        </span>
                      </div>
                      <div className="cell-style">
                        <span className="time-text">
                          {item.chat_with_email ? (
                            <span className="time-text">
                              {item.chat_with_email}
                            </span>
                          ) : (
                            ""
                          )}
                        </span>

                        {item.unread_messages_count > 0 && (
                          <span className="rounded-pill red">
                            {item.unread_messages_count}
                          </span>
                        )}
                      </div>
                      <div className="cell-style">
                        <span className="description-text">
                          {item.latest_message
                            ? item.latest_message.body.length > 25
                              ? item.latest_message.body.substring(0, 25) +
                                "..."
                              : item.latest_message.body
                            : " "}
                        </span>
                        {PermissionCheck("message-thread-delete") ||
                        PermissionCheck("message-thread-update")
                          ? userData.id === creator && (
                              <Dropdown
                                isOpen={dropdownId === item.id ? true : false}
                                toggle={() => setDropdownId(null)}
                              >
                                <DropdownToggle caret={false} tag="span">
                                  <span
                                    className="d-inline-flex"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDropdownId(item.id);
                                    }}
                                  >
                                    <i className="icon three-dots-icon" />
                                  </span>
                                </DropdownToggle>
                                <DropdownMenu className="dropdown-menu-end">
                                  {item.type === "group" ? (
                                    <>
                                      <DropdownItem
                                        key={item.id}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowEditThread(true);
                                          setModalId(item.id);
                                          e.preventDefault();
                                        }}
                                      >
                                        <i className="icon edit-icon me-2" />
                                        <span>{t("gallery_edit")}</span>
                                      </DropdownItem>
                                      {modalId === item.id &&
                                        showEditThread && (
                                          <EditThread
                                            showModal={showEditThread}
                                            setShowModal={(e) => {
                                              setShowEditThread(e);
                                              setModalId(null);
                                            }}
                                            setModified={() => getChatList()}
                                            threadId={item.id}
                                          />
                                        )}
                                    </>
                                  ) : (
                                    ""
                                  )}

                                  {item.type === "group" ? (
                                    <DropdownItem
                                      key={item.id}
                                      onClick={(e) => {
                                        deleteThread(item.id, item.subject);
                                        e.stopPropagation();
                                      }}
                                    >
                                      <i className="icon delete-icon me-2" />
                                      <span>{t("gallery_delete")}</span>
                                    </DropdownItem>
                                  ) : (
                                    <DropdownItem
                                      key={item.id}
                                      onClick={(e) => {
                                        deleteThread(item.id, item.chat_with);
                                        e.stopPropagation();
                                      }}
                                    >
                                      <i className="icon delete-icon me-2" />
                                      <span>{t("gallery_delete")}</span>
                                    </DropdownItem>
                                  )}
                                </DropdownMenu>
                              </Dropdown>
                            )
                          : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </SimpleBar>
          ) : (
            ""
          )}
        </div>
        {PermissionCheck("message-list-in-thread") ? (
          <>
            {threadId ? (
              <ThreadMessages
                threadId={threadId}
                getChatList={() => getChatList()}
              />
            ) : (
              <div className="no-conversation">
                <div className="image-holder">
                  <img
                    className="d-flex m-auto"
                    src={ChatLogo}
                    alt="ChatLogo"
                  />
                  <div className="text">
                    <span className="text-center">
                      {t("chat_choose_conversation")}
                    </span>
                    <span className="sub-text text-center">
                      {t("chat_no_conversation_chosen")}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          ""
        )}
      </div>
    </>
  );
};

export default Chat;
